import { createBrowserRouter, Navigate } from 'react-router';
import { AppShell } from '../layouts/AppShell';
import { RequireAuth, RequirePermission } from '../features/auth/RequireAuth';

/**
 * Route table.
 *
 * Every page is lazily imported, so the initial download is the shell plus the one
 * screen being visited rather than the whole application. That is what keeps the
 * bundle inside the 180 KB budget as features land in later phases.
 *
 * The nesting encodes the access rules:
 *
 *   public          → /login, /forgot-password, /reset-password
 *   RequireAuth     → everything inside the app shell
 *   RequirePermission → /admin, gated on `user.list`
 *
 * `RequirePermission` renders a refusal rather than redirecting: the user IS signed
 * in, so bouncing them to /login would misreport what happened — and would loop,
 * because the login page would see a valid session and send them straight back.
 */
export const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/boards" replace /> },

  {
    path: '/login',
    lazy: async () => ({ Component: (await import('../pages/auth/LoginPage')).default }),
  },
  {
    path: '/forgot-password',
    lazy: async () => ({ Component: (await import('../pages/auth/ForgotPasswordPage')).default }),
  },
  {
    path: '/reset-password',
    lazy: async () => ({ Component: (await import('../pages/auth/ResetPasswordPage')).default }),
  },

  /**
   * The design system showcase is deliberately PUBLIC.
   *
   * It renders no data of any kind — only primitives and tokens — so there is
   * nothing to protect, and keeping it reachable lets the automated accessibility
   * suite check every primitive in both themes without first standing up a database
   * and a seeded account. That trade buys real regression coverage for zero
   * exposure. If it ever starts rendering real content, it moves inside RequireAuth.
   */
  {
    path: '/ui-kit',
    lazy: async () => ({ Component: (await import('../pages/UiKitPage')).default }),
  },

  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            path: '/boards',
            lazy: async () => ({ Component: (await import('../pages/boards/BoardsPage')).default }),
          },
          {
            path: '/boards/:boardId',
            lazy: async () => ({ Component: (await import('../pages/boards/BoardPage')).default }),
            children: [
              {
                // A card opens as a modal over the board; the board stays mounted and
                // renders this through its outlet.
                path: 'cards/:cardId',
                lazy: async () => ({
                  Component: (await import('../pages/boards/CardModal')).default,
                }),
              },
            ],
          },
          {
            path: '/search',
            lazy: async () => ({ Component: (await import('../pages/search/SearchPage')).default }),
          },
          {
            path: '/settings',
            lazy: async () => ({
              Component: (await import('../pages/settings/SettingsPage')).default,
            }),
          },
          {
            element: <RequirePermission permission="user.list" />,
            children: [
              {
                path: '/admin',
                lazy: async () => ({
                  Component: (await import('../pages/admin/AdminPage')).default,
                }),
              },
            ],
          },
        ],
      },
    ],
  },

  {
    path: '*',
    lazy: async () => ({ Component: (await import('../pages/errors/NotFoundPage')).default }),
  },
]);
